import { useState } from 'react';
import ReportsService from '../Services/reports.service';

export const usePeriodComparison = (client, token, isProfitAndLoss = true, trialBalanceExtraFilter = null) => {
  // Period comparison states
  const [showPeriodComparison, setShowPeriodComparison] = useState(false);
  const [periodComparisonData, setPeriodComparisonData] = useState({
    period1: null,
    period2: null,
    data1: null,
    data2: null
  });
  const [showPeriodSelectionModal, setShowPeriodSelectionModal] = useState(false);
  const [selectedPeriod1, setSelectedPeriod1] = useState({ year: new Date().getFullYear() -1, startMonth: 1, endMonth: 12 });
  const [selectedPeriod2, setSelectedPeriod2] = useState({ year: new Date().getFullYear(), startMonth: 1, endMonth: 12 });
  const [isLoadingComparison, setIsLoadingComparison] = useState(false);

  /**
   * Fetch data for a specific period
   */
  const fetchPeriodData = async (period) => {
    try {
      // Create start and end dates for the period
      const startDate = new Date(Date.UTC(period.year, period.startMonth - 1, 1));
      const endDate = new Date(Date.UTC(period.year, period.endMonth, 0)); // Last day of end month

      // Call the same API that LedgerReport uses
      const response = await ReportsService.GetTrialBalanceDetails(
        client.caseId,
        client.accountantId,
        startDate,
        endDate,
        false, // isAnnual - false for monthly periods
        token,
        true, // onlyProcessed
        isProfitAndLoss,  // isProfitAndLoss - passed as parameter
        trialBalanceExtraFilter && trialBalanceExtraFilter.trialBalanceType === 'Projects'
          ? trialBalanceExtraFilter
          : null,
        true // filterByDocumentDate
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch data: ${errorText}`);
      }

      const responseJson = await response.json();
      return responseJson;
    } catch (error) {
      console.error('Error fetching period data:', error);
      throw error;
    }
  };

  /**
   * Handle opening period selection modal
   */
  const handleOpenPeriodSelection = () => {
    setShowPeriodSelectionModal(true);
  };

  /**
   * Handle closing period selection modal
   */
  const handleClosePeriodSelection = () => {
    setShowPeriodSelectionModal(false);
  };

  /**
   * Handle fetching comparison data for both periods
   */
  const handleFetchComparisonData = async () => {
    setIsLoadingComparison(true);
    try {
      // Fetch data for both periods
      const [data1, data2] = await Promise.all([
        fetchPeriodData(selectedPeriod1),
        fetchPeriodData(selectedPeriod2)
      ]);

      setPeriodComparisonData({
        period1: selectedPeriod1,
        period2: selectedPeriod2,
        data1: data1,
        data2: data2
      });

      setShowPeriodComparison(true);
      setShowPeriodSelectionModal(false);

      return { data1, data2 };
    } catch (error) {
      console.error('Error fetching comparison data:', error);
      throw error;
    } finally {
      setIsLoadingComparison(false);
    }
  };

  /**
   * Handle closing period comparison
   */
  const handleCloseComparison = () => {
    setShowPeriodComparison(false);
    setPeriodComparisonData({
      period1: null,
      period2: null,
      data1: null,
      data2: null
    });
  };

  return {
    // State
    showPeriodComparison,
    periodComparisonData,
    showPeriodSelectionModal,
    selectedPeriod1,
    selectedPeriod2,
    isLoadingComparison,
    
    // State setters
    setSelectedPeriod1,
    setSelectedPeriod2,
    
    // Actions
    handleOpenPeriodSelection,
    handleClosePeriodSelection,
    handleFetchComparisonData,
    handleCloseComparison
  };
};
